/**
 * Typed wrappers over the Gateway RPC methods the messenger uses.
 *
 * Keeping every method name and parameter shape in one file means the rest of
 * the app never builds a raw frame, and a protocol change has exactly one place
 * to land.
 *
 * Every call goes through `callCompat`, which sheds optional params a given
 * Gateway build does not know (its param objects are closed, so an unknown
 * property fails the whole call). The second argument list names the params that
 * must never be shed, because dropping one would turn a rejected call into a
 * silently wrong one.
 */
import type { OutgoingAttachment } from "./attachments";
import { callCompat } from "./compat";
import { HISTORY_PAGE_SIZE, SEARCH_PAGE_SIZE, SESSION_PAGE_SIZE } from "./constants";
import type { GatewayConnection } from "./connection";
import type {
  ArtifactsDownloadResult,
  ChatHistoryResult,
  SessionRow,
  SessionsCreateResult,
  ModelsListResult,
  SessionsListResult,
  SessionsSearchResult,
  TtsSpeakResult,
} from "./types";

export type SessionScope = "active" | "archived";

/** Reads the session index the conversation list renders. */
export async function listSessions(
  connection: GatewayConnection,
  scope: SessionScope = "active",
): Promise<SessionRow[]> {
  const result = await callCompat<SessionsListResult>(
    connection,
    "sessions.list",
    {
      limit: SESSION_PAGE_SIZE,
      sortBy: "lastInteractionAt",
      // Titles and previews are what makes the list readable at a glance; both are
      // bounded transcript projections the Gateway only computes when asked. If a
      // build does not offer them the list still renders, just plainer.
      includeDerivedTitles: true,
      includeLastMessage: true,
      includeGlobal: false,
      includeUnknown: false,
      ...(scope === "archived" ? { archived: true } : {}),
    },
    // Shedding `archived` would fill the archive tab with active chats.
    ["archived"],
  );
  return result?.sessions ?? [];
}

export async function createSession(
  connection: GatewayConnection,
  params: { label?: string; message?: string } = {},
): Promise<SessionsCreateResult> {
  return await callCompat<SessionsCreateResult>(
    connection,
    "sessions.create",
    {
      ...(params.label ? { label: params.label } : {}),
      ...(params.message ? { message: params.message } : {}),
    },
    ["label", "message"],
  );
}

export async function loadHistory(
  connection: GatewayConnection,
  sessionKey: string,
  options: { limit?: number; offset?: number } = {},
): Promise<ChatHistoryResult> {
  return await callCompat<ChatHistoryResult>(
    connection,
    "chat.history",
    {
      sessionKey,
      limit: options.limit ?? HISTORY_PAGE_SIZE,
      // The protocol documents `offset` as a paging position but not which end
      // it counts from, so the store never trusts it blindly: it keeps only the
      // rows it does not already have and stops when a page adds nothing.
      ...(options.offset ? { offset: options.offset } : {}),
    },
    ["sessionKey"],
  );
}

export async function sendMessage(
  connection: GatewayConnection,
  params: {
    sessionKey: string;
    message: string;
    idempotencyKey: string;
    attachments?: readonly OutgoingAttachment[];
    /**
     * What a send should do to a run that is already going: `steer` folds it
     * into the running turn, `followup` queues it behind, `interrupt` stops the
     * run and starts fresh. Omitted when nothing is running.
     */
    queueMode?: "steer" | "followup" | "interrupt";
  },
): Promise<unknown> {
  // `idempotencyKey` is required by ChatSendParamsSchema: it is what lets a
  // resend after a flaky reconnect land as the same turn instead of a duplicate.
  return await callCompat(
    connection,
    "chat.send",
    {
      sessionKey: params.sessionKey,
      message: params.message,
      idempotencyKey: params.idempotencyKey,
      ...(params.queueMode ? { queueMode: params.queueMode } : {}),
      ...(params.attachments?.length
        ? {
            // The wire shape the Gateway parser expects: a `data:` URL in
            // `content`, with the metadata beside it.
            attachments: params.attachments.map((item) => ({
              type: item.type,
              mimeType: item.mimeType,
              fileName: item.fileName,
              content: item.content,
              sizeBytes: item.sizeBytes,
              ...(item.width ? { width: item.width, height: item.height } : {}),
            })),
          }
        : {}),
    },
    // `attachments` is protected on purpose: shedding it on a Gateway that does
    // not know the param would send the text alone and lose the picture without
    // a word. Better a visible failure than a silent one.
    // `queueMode` changes what happens to a running turn; shedding it would
    // quietly do something other than what the user picked.
    ["sessionKey", "message", "idempotencyKey", "attachments", "queueMode"],
  );
}

/**
 * Fetches the bytes behind an artifact-backed attachment.
 *
 * Assistant images arrive as a reference, not as data. The download rides the
 * authenticated WebSocket, which is what keeps it working against a Gateway
 * behind a reverse proxy without a second set of credentials or a CORS dance.
 */
export async function downloadArtifact(
  connection: GatewayConnection,
  params: { artifactId: string; sessionKey?: string },
): Promise<ArtifactsDownloadResult | null> {
  return await callCompat<ArtifactsDownloadResult>(
    connection,
    "artifacts.download",
    {
      artifactId: params.artifactId,
      ...(params.sessionKey ? { sessionKey: params.sessionKey } : {}),
    },
    ["artifactId"],
  );
}

/**
 * Turns text into audio on the Gateway.
 *
 * The one piece of voice that needs no new scope: `tts.speak` is
 * `operator.write`, which this app already holds, and it answers with the bytes
 * rather than playing them on the Gateway host. Realtime voice lives behind
 * `operator.talk` and a whole session protocol — see the README.
 */
export async function speakText(
  connection: GatewayConnection,
  text: string,
): Promise<TtsSpeakResult | null> {
  return await callCompat<TtsSpeakResult>(connection, "tts.speak", { text }, ["text"]);
}

/** Probes whether this Gateway has speech configured at all. */
export async function ttsStatus(connection: GatewayConnection): Promise<unknown> {
  return await callCompat(connection, "tts.status", {}, []);
}

/**
 * Lists the models this Gateway can actually run.
 *
 * `view: "configured"` keeps the list to what the operator has set up rather
 * than the provider's whole catalogue — a picker is only useful if everything
 * in it can be chosen.
 */
export async function listModels(connection: GatewayConnection): Promise<ModelsListResult | null> {
  return await callCompat<ModelsListResult>(connection, "models.list", { view: "configured" }, []);
}

/** Pins one conversation to a model. `null` hands it back to the default. */
export async function setSessionModel(
  connection: GatewayConnection,
  params: { key: string; model: string | null },
): Promise<unknown> {
  return await callCompat(
    connection,
    "sessions.patch",
    { key: params.key, model: params.model },
    ["key", "model"],
  );
}

/**
 * Searches message text across every conversation.
 *
 * The list's own filter only ever sees the page of sessions already loaded and
 * matches titles; this reaches the transcripts themselves. `indexing: true`
 * means the Gateway is still building its index, so an empty result is "not
 * yet" rather than "nothing".
 */
export async function searchMessages(
  connection: GatewayConnection,
  params: { query: string; limit?: number },
): Promise<SessionsSearchResult | null> {
  return await callCompat<SessionsSearchResult>(
    connection,
    "sessions.search",
    { query: params.query, limit: params.limit ?? SEARCH_PAGE_SIZE },
    ["query"],
  );
}

/**
 * Web Push: the key, the registration, and the way back out.
 *
 * The Gateway holds the VAPID key pair and does the sending; the browser only
 * ever hands over an endpoint and two public values. Nothing here is secret,
 * which is what makes it safe to do from a client with no backend of its own.
 */
export async function vapidPublicKey(connection: GatewayConnection): Promise<string | null> {
  const result = await callCompat<{ publicKey?: string; key?: string }>(
    connection,
    "push.web.vapidPublicKey",
    {},
    [],
  );
  return result?.publicKey ?? result?.key ?? null;
}

export async function subscribePush(
  connection: GatewayConnection,
  subscription: { endpoint: string; keys: { p256dh: string; auth: string } },
): Promise<unknown> {
  return await callCompat(connection, "push.web.subscribe", subscription, [
    "endpoint",
    "keys",
  ]);
}

export async function unsubscribePush(
  connection: GatewayConnection,
  endpoint: string,
): Promise<unknown> {
  return await callCompat(connection, "push.web.unsubscribe", { endpoint }, ["endpoint"]);
}

/** Asks the Gateway to deliver one notification, so the user can see it work. */
export async function testPush(connection: GatewayConnection): Promise<unknown> {
  return await callCompat(
    connection,
    "push.web.test",
    { title: "cllw", body: "Benachrichtigungen sind eingerichtet." },
    [],
  );
}

export async function abortRun(
  connection: GatewayConnection,
  sessionKey: string,
): Promise<unknown> {
  return await callCompat(connection, "chat.abort", { sessionKey }, ["sessionKey"]);
}

export async function renameSession(
  connection: GatewayConnection,
  params: { key: string; label: string | null },
): Promise<unknown> {
  return await callCompat(
    connection,
    "sessions.patch",
    { key: params.key, label: params.label },
    ["key", "label"],
  );
}

export async function setSessionArchived(
  connection: GatewayConnection,
  params: { key: string; archived: boolean; expectedSessionId?: string },
): Promise<unknown> {
  // Archive and restore are compare-and-swap on the caller-observed sessionId:
  // without it the Gateway refuses rather than mutating a replaced session. A
  // build that does not know the guard simply archives without it.
  return await callCompat(
    connection,
    "sessions.patch",
    {
      key: params.key,
      archived: params.archived,
      ...(params.expectedSessionId ? { expectedSessionId: params.expectedSessionId } : {}),
    },
    ["key", "archived"],
  );
}

export async function setSessionPinned(
  connection: GatewayConnection,
  params: { key: string; pinned: boolean },
): Promise<unknown> {
  return await callCompat(
    connection,
    "sessions.patch",
    { key: params.key, pinned: params.pinned },
    ["key", "pinned"],
  );
}

export async function deleteSession(
  connection: GatewayConnection,
  params: { key: string; archivedOnly?: boolean },
): Promise<unknown> {
  return await callCompat(
    connection,
    "sessions.delete",
    {
      key: params.key,
      deleteTranscript: true,
      ...(params.archivedOnly ? { archivedOnly: true } : {}),
    },
    // `archivedOnly` is what lets an operator.write caller delete at all: without
    // it the Gateway demands operator.admin. Shedding it would turn a working
    // delete into a permission error.
    ["key", "archivedOnly"],
  );
}

export async function subscribeSessionMessages(
  connection: GatewayConnection,
  sessionKey: string,
): Promise<unknown> {
  return await callCompat(connection, "sessions.messages.subscribe", { sessionKey }, [
    "sessionKey",
  ]);
}

export async function unsubscribeSessionMessages(
  connection: GatewayConnection,
  sessionKey: string,
): Promise<unknown> {
  return await callCompat(connection, "sessions.messages.unsubscribe", { sessionKey }, [
    "sessionKey",
  ]);
}
